import { motion } from "framer-motion";

const InstituteVendor = ({
  scrollToForm,
  billingCycle,
  setBillingCycle,
  plans,
}) => {
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 space-y-12">

        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col items-center text-center space-y-4"
        >
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
            Empower Your{" "}
            <span className="text-blue-600">RPTO Growth</span> with Consistent,
            High-Quality Student Leads
          </h1>

          <p className="text-lg text-gray-600 max-w-2xl">
            Tap into a nationwide pipeline for reliable, pre-qualified leads
            every month. Claim your first{" "}
            <span className="font-semibold text-blue-700">5 leads free!</span>
          </p>

          {/* <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToForm}
            className="px-8 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold shadow-md"
          >
            Get Started
          </motion.button> */}
        </motion.section>

        {/* Why Choose Us */}
        <section>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Why Choose Our Subscription?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: "🎯",
                title: "Market Expertise & Precision",
                desc: "We connect you with pre-qualified drone students using advanced targeting algorithms.",
              },
              {
                icon: "📈",
                title: "Predictable, Scalable Growth",
                desc: "Stay consistent with monthly leads and scale confidently with demand.",
              },
              {
                icon: "✅",
                title: "Quality Assurance",
                desc: "Every lead is verified for genuine interest and basic qualifications.",
              },
            ].map((c, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -6, scale: 1.02 }}
                className="bg-white p-6 rounded-2xl shadow-lg text-center"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 text-blue-600 rounded-xl text-2xl mx-auto mb-3">
                  {c.icon}
                </div>

                <h3 className="text-lg font-semibold text-gray-800">
                  {c.title}
                </h3>

                <p className="text-gray-600 mt-2 text-sm">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            How It Works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: "1",
                title: "Define Your Target Profile",
                desc: "We align demographics, region, certification type & budget range.",
              },
              {
                step: "2",
                title: "Receive & Review Leads",
                desc: "Get leads in your dashboard — act within 48 hours.",
              },
              {
                step: "3",
                title: "Engage & Convert",
                desc: "Use proven scripts and templates to boost conversions by 30%.",
              },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="bg-white p-6 rounded-2xl shadow-md text-center relative"
              >
                <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 
                w-14 h-14 flex items-center justify-center 
                bg-blue-600 text-white rounded-full 
                font-bold text-xl shadow-lg">
                  {s.step}
                </span>

                <h3 className="text-md font-semibold text-gray-900 mt-5">
                  {s.title}
                </h3>

                <p className="text-gray-600 mt-1 text-sm">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Pricing */}
        <section>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Select Your Package
          </h2>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-6">
            <div className="flex space-x-2 bg-gray-100 p-1 rounded-full">
              {["monthly", "annually"].map((cycle) => (
                <button
                  key={cycle}
                  onClick={() => setBillingCycle(cycle)}
                  className={`px-3 py-1 text-sm font-medium rounded-full transition ${
                    billingCycle === cycle
                      ? "bg-blue-600 text-white"
                      : "text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {cycle === "annually" ? "Annually (30% OFF)" : "Monthly"}
                </button>
              ))}
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -6, scale: 1.02 }}
                className={`bg-white rounded-xl shadow-lg p-5 flex flex-col ${
                  plan.popular ? "ring-2 ring-blue-600" : ""
                }`}
              >
                <div className="flex-grow">
                  <h3 className="text-lg font-bold">{plan.title}</h3>

                  <p className="text-2xl font-extrabold mt-1">
                    ₹{plan.price}
                    <span className="text-gray-500 text-xs">/month</span>
                  </p>

                  <ul className="mt-4 space-y-2 text-sm text-gray-600">
                    {plan.features.map((f, idx) => (
                      <li key={idx}>✅ {f}</li>
                    ))}
                  </ul>
                </div>

                {/* <div className="mt-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-full font-medium text-sm"
                  >
                    Subscribe
                  </motion.button>
                </div> */}
              </motion.div>
            ))}
          </div>

          {/* Custom Contact */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="mt-8 text-center"
          >
            <h3 className="text-lg font-semibold text-blue-700">
              Need something custom?
            </h3>

            <p className="text-sm text-gray-600 mb-3">
              Let’s talk about a tailored solution for your organization.
            </p>

            <a
              href="https://wa.me/919773571992"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-5 rounded-full font-medium text-sm"
            >
              Contact Sales
            </a>
          </motion.div>
        </section>

      </div>
    </div>
  );
};

export default InstituteVendor;